import { ClientError } from '@ricoh/ria.client.core/errors';
import { isNull } from '@ricoh/ria.client.core/utils/data/object';

import type { Batch, BatchView, Document, DocumentFieldWithValue, FieldValue } from '~/common/model/api/batch';

import fieldLogic from '~/common/logic/feature/settings/batch-type-library/field';

import type { DocumentFieldsFormField } from './_document.type';

/**
 * Get Document number from Document name.
 *
 * @param {string} name Document name.
 * @param {string} documentNamePrefix DocumentName prefix.
 *
 * @returns {number | undefined} Document number.
 */
export function getDocumentNumber(name: string, documentNamePrefix: string): number | undefined {
  const documentNameRegex = new RegExp(`^${documentNamePrefix} (\\d+)$`);
  const match = documentNameRegex.exec(name);
  if (!match) {
    return undefined;
  }

  const number = Number(match[1]);
  return Number.isNaN(number) ? undefined : number;
}

/**
 * Get current document naming number.
 *
 * @param {Batch | BatchView} batch Batch.
 * @param {string} documentNamePrefix DocumentName prefix.
 *
 * @returns {number} Current document naming number.
 */
export function getCurrentDocumentNumber(batch: Batch | BatchView, documentNamePrefix: string): number {
  let current = 0;
  if (batch.documents.length) {
    current = Math.max(
      ...batch.documents.map(({ name = '' }) => {
        const number = getDocumentNumber(name, documentNamePrefix);

        return isNull(number) ? 0 : number;
      }),
    );
  }

  return current;
}

/**
 * Create Document name.
 *
 * @param {string} documentNamePrefix DocumentName prefix.
 * @param {number} documentNumber Document number.
 *
 * @returns {string} Document name.
 */
export function createDocumentName(documentNamePrefix: string, documentNumber: number): string {
  return `${documentNamePrefix} ${documentNumber}`;
}

/**
 * Create DocumentFieldsWithValue FormField.
 *
 * @param {DocumentFieldWithValue[]} documentFields DocumentFields with value.
 *
 * @returns {DocumentFieldsFormField} DocumentFieldsWithValue FormField.
 */
export function createDocumentFieldsWithValueFormField(documentFields: DocumentFieldWithValue[]): DocumentFieldsFormField {
  return documentFields.reduce<DocumentFieldsFormField>((prev, { id, type, value, ocr }) => {
    prev[id] = {
      isValidValue: !value?.metadata?.errors?.length,
      isValidConfidence: ocr.isValidConfidence,
      value: fieldLogic.createDataValueFormField(value, type),
    };

    return prev;
  }, {});
}

/**
 * Create DocumentFieldsWithValue from FormField.
 *
 * @param {Document} document Document.
 * @param {DocumentFieldsFormField} documentFields DocumentFields FormField.
 * @param {boolean} shouldStrict ShouldStrict.
 *
 * @returns {DocumentFieldWithValue[]} DocumentFieldsWithValue.
 */
export function createDocumentFieldsWithValue(
  document: Document,
  documentFields: DocumentFieldsFormField,
  shouldStrict: boolean = true,
): DocumentFieldWithValue[] {
  return document.fields.map<DocumentFieldWithValue>((documentField) => {
    const field = documentFields[documentField.id];

    if (!field) {
      let value: FieldValue = fieldLogic.createDataValue(documentField.type, undefined);
      if (documentField.value?.metadata) {
        value = {
          ...value,
          metadata: documentField.value.metadata,
        };
      }

      if (shouldStrict) {
        throw new ClientError({
          module: 'logic',
          message: 'DocumentField not found from FormField',
          cause: documentField,
        });
      }

      return {
        ...documentField,
        value,
      };
    }

    return {
      ...documentField,
      value: fieldLogic.createDataValue(documentField.type, field.value),
      ocr: {
        ...documentField.ocr,
        isValidConfidence: field.isValidConfidence,
      },
    };
  });
}
